import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Card, Grid, Stack, Button, 
  Chip, Avatar, Paper, Alert, AlertTitle, CircularProgress,
  useTheme, useMediaQuery
} from '@mui/material';
import { 
  AccountBalanceWallet, ShoppingCart, Star, 
  CardGiftcard, WarningAmber, ErrorOutline, 
  MonetizationOn, AddShoppingCart
} from '@mui/icons-material';

// --- Premium Theme Config ---
const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
const darkGradient = 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)';
const lightBg = '#F8FAFC';

// --- Helper: Load Razorpay Script in React ---
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// 🌟 DUMMY DATA (Replace with actual props/API data) 🌟
const dummyWallet = {
  daily_free_credit: 3,
  purchased_balance: 145,
  total_available: 148,
  show_urgent_warning: true, // Set to true to see the red alert
  show_expiration_warning: false,
  credits_expiring_soon: 45,
  nearest_expiration: '28 Feb 2026'
};

const dummyPlans = [
  { id: 'plan_1', name: 'बेसिक पैक / Basic Pack', total_credits: 100, bonus_credits: 0, description: 'छोटे अभियानों के लिए उपयुक्त (Ideal for small campaigns)', price_rupees: 99, is_popular: false },
  { id: 'plan_2', name: 'सुपर पैक / Super Pack', total_credits: 500, bonus_credits: 50, description: 'सबसे ज्यादा बिकने वाला (Most value for money)', price_rupees: 399, is_popular: true },
  { id: 'plan_3', name: 'मेगा पैक / Mega Pack', total_credits: 1000, bonus_credits: 200, description: 'बड़े चुनावों के लिए (Perfect for heavy usage)', price_rupees: 799, is_popular: false },
];

export default function BuyCredits() {
  const [processingPlanId, setProcessingPlanId] = useState(null);

  // 🌟 Handle Purchase Logic (Razorpay Integration) 🌟
  const handlePurchase = async (planId, credits, price) => {
    setProcessingPlanId(planId);

    try {
      // 1. Load Razorpay script dynamically
      const res = await loadRazorpayScript();
      if (!res) {
        alert("Razorpay SDK failed to load. Are you online?");
        setProcessingPlanId(null);
        return;
      }

      // 2. Call your backend to create the order
      // (Uncomment this in production and replace with your actual API endpoint)
      /*
      const response = await fetch('/api/payments/credits/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credit_plan_id: planId })
      });
      const data = await response.json();
      */

      // 🛑 SIMULATED BACKEND RESPONSE FOR TESTING 🛑
      const data = {
        status: 'success',
        data: {
          razorpay_key_id: 'rzp_test_YourTestKeyHere', // Replace with real key in prod if doing frontend init
          amount: price,
          currency: 'INR',
          razorpay_order_id: 'order_dummy_12345'
        }
      };

      if (data.status === 'success') {
        const options = {
          key: data.data.razorpay_key_id,
          amount: data.data.amount * 100, // paise
          currency: data.data.currency || 'INR',
          name: 'PracharApp',
          description: `Purchase ${credits} Credits`,
          order_id: data.data.razorpay_order_id,
          handler: async function (response) {
            // 3. Verify payment via backend
            try {
              /*
              const verifyResponse = await fetch('/api/payments/credits/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                })
              });
              const verifyData = await verifyResponse.json();
              */

              // Simulate success
              alert('Payment successful! Credits added to your wallet.');
              // window.location.reload(); 
            } catch (error) {
              console.error('Verification error:', error);
              alert('Payment verification failed.');
            } finally {
              setProcessingPlanId(null);
            }
          },
          prefill: {
            name: 'User Name', // Replace with actual user context
            email: 'user@example.com',
            contact: '9876543210'
          },
          theme: {
            color: '#FF6A00' // Matches PracharApp Theme
          },
          modal: {
            ondismiss: function() {
              setProcessingPlanId(null); // Stop loading if user closes modal
            }
          }
        };

        const paymentObject = new window.Razorpay(options);
        paymentObject.on('payment.failed', function (response) {
          alert('Payment failed. Please try again.');
          setProcessingPlanId(null);
        });
        paymentObject.open();

      } else {
        alert('Failed to initiate purchase: ' + (data.message || 'Unknown error'));
        setProcessingPlanId(null);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('An error occurred. Please try again.');
      setProcessingPlanId(null);
    }
  };

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 12 }}>
      
      {/* 🌟 HERO SECTION 🌟 */}
      <Box sx={{ pt: { xs: 8, md: 10 }, pb: { xs: 10, md: 12 }, background: darkGradient, position: 'relative', textAlign: 'center' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '3px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(238, 9, 121, 0.4)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <ShoppingCart sx={{ fontSize: { xs: 50, md: 60 }, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h3" fontWeight="900" color="white" sx={{ fontSize: { xs: '1.8rem', md: '2.8rem' }, mb: 1 }}>
            क्रेडिट खरीदें / Buy Credits
          </Typography>
          <Typography variant="body1" color="#94A3B8" fontWeight="500">
            अपने क्रेडिट बैलेंस को रिचार्ज करें और डिज़ाइन बनाना जारी रखें <br /> Recharge your credit balance and continue creating designs
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -5, md: -6 }, position: 'relative', zIndex: 2 }}>
        
        {/* 🌟 EXPIRATION WARNINGS 🌟 */}
        {dummyWallet && dummyWallet.show_urgent_warning && (
          <Alert 
            icon={<ErrorOutline fontSize="inherit" />} 
            severity="error" 
            sx={{ mb: 3, borderRadius: 3, bgcolor: '#FEF2F2', border: '1px solid #FCA5A5', '& .MuiAlert-icon': { color: '#EF4444' } }}
          >
            <AlertTitle sx={{ fontWeight: 'bold' }}>{dummyWallet.credits_expiring_soon} credits expiring soon!</AlertTitle>
            Use them before <strong>{dummyWallet.nearest_expiration}</strong>
          </Alert>
        )}
        
        {dummyWallet && !dummyWallet.show_urgent_warning && dummyWallet.show_expiration_warning && (
          <Alert 
            icon={<WarningAmber fontSize="inherit" />} 
            severity="warning" 
            sx={{ mb: 3, borderRadius: 3, bgcolor: '#FFFBEB', border: '1px solid #FCD34D', '& .MuiAlert-icon': { color: '#F59E0B' } }}
          >
            <AlertTitle sx={{ fontWeight: 'bold' }}>{dummyWallet.credits_expiring_soon} credits expiring</AlertTitle>
            on <strong>{dummyWallet.nearest_expiration}</strong>
          </Alert>
        )}

        {/* 🌟 CURRENT BALANCE BREAKDOWN (Compact Row for Mobile/Desktop) 🌟 */}
        {dummyWallet && (
          <Card sx={{ p: 0, borderRadius: 4, boxShadow: '0 8px 25px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0', mb: 5, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #E2E8F0', textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="800" color="#1E293B" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccountBalanceWallet sx={{ mr: 1, color: '#3B82F6' }} /> Credit Balance / क्रेडिट बैलेंस
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'row', bgcolor: 'white' }}>
              {/* Daily Free */}
              <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, textAlign: 'center', borderRight: '1px solid #E2E8F0' }}>
                <CardGiftcard sx={{ color: '#10B981', fontSize: { xs: 28, md: 36 }, mb: 1 }} />
                <Typography variant="h4" fontWeight="900" color="#10B981" sx={{ fontSize: { xs: '1.4rem', md: '2.2rem' }, lineHeight: 1 }}>
                  {dummyWallet.daily_free_credit}<span style={{fontSize: '1rem', color: '#94A3B8'}}>/5</span>
                </Typography>
                <Typography variant="caption" color="#64748B" fontWeight="700" sx={{ fontSize: { xs: '0.65rem', md: '0.85rem' }, mt: 0.5, display: 'block' }}>
                  Daily Free<br/>दैनिक मुफ्त
                </Typography>
              </Box>

              {/* Purchased */}
              <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, textAlign: 'center', borderRight: '1px solid #E2E8F0' }}>
                <MonetizationOn sx={{ color: '#F59E0B', fontSize: { xs: 28, md: 36 }, mb: 1 }} />
                <Typography variant="h4" fontWeight="900" color="#F59E0B" sx={{ fontSize: { xs: '1.4rem', md: '2.2rem' }, lineHeight: 1 }}>
                  {dummyWallet.purchased_balance}
                </Typography>
                <Typography variant="caption" color="#64748B" fontWeight="700" sx={{ fontSize: { xs: '0.65rem', md: '0.85rem' }, mt: 0.5, display: 'block' }}>
                  Purchased<br/>खरीदे गए
                </Typography>
              </Box>

              {/* Total */}
              <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, textAlign: 'center' }}>
                <Star sx={{ color: '#3B82F6', fontSize: { xs: 28, md: 36 }, mb: 1 }} />
                <Typography variant="h4" fontWeight="900" color="#3B82F6" sx={{ fontSize: { xs: '1.4rem', md: '2.2rem' }, lineHeight: 1 }}>
                  {dummyWallet.total_available}
                </Typography>
                <Typography variant="caption" color="#64748B" fontWeight="700" sx={{ fontSize: { xs: '0.65rem', md: '0.85rem' }, mt: 0.5, display: 'block' }}>
                  Total Available<br/>कुल उपलब्ध
                </Typography>
              </Box>
            </Box>
          </Card>
        )}

        {/* 🌟 CREDIT PLANS 🌟 */}
        <Typography variant="h5" fontWeight="900" color="#1E293B" textAlign="center" mb={4}>
          Available Credit Plans / उपलब्ध क्रेडिट प्लान
        </Typography>

        {dummyPlans && dummyPlans.length > 0 ? (
          <Grid container spacing={3} justifyContent="center" mb={10}>
            {dummyPlans.map((plan) => {
              const isProcessing = processingPlanId === plan.id;
              
              return (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <Card 
                    sx={{ 
                      height: '100%', borderRadius: 4, position: 'relative', overflow: 'visible',
                      border: plan.is_popular ? '2px solid #FF6A00' : '1px solid #E2E8F0',
                      boxShadow: plan.is_popular ? '0 15px 35px rgba(255, 106, 0, 0.15)' : '0 4px 15px rgba(0,0,0,0.03)',
                      transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' },
                      display: 'flex', flexDirection: 'column'
                    }}
                  >
                    {plan.is_popular && (
                      <Chip 
                        label="MOST POPULAR" 
                        icon={<Star style={{ color: 'white' }} fontSize="small" />}
                        sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: primaryGradient, color: 'white', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(238,9,121,0.3)' }} 
                      />
                    )}

                    <Box sx={{ p: 4, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', mt: plan.is_popular ? 1 : 0 }}>
                      <Typography variant="h6" fontWeight="800" color="#1E293B" mb={2}>{plan.name}</Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h2" fontWeight="900" color={plan.is_popular ? '#FF6A00' : '#3B82F6'} lineHeight={1}>
                          {plan.total_credits}
                        </Typography>
                        <Typography variant="subtitle2" color="#64748B" fontWeight="600" mt={0.5}>Credits</Typography>
                        
                        {plan.bonus_credits > 0 && (
                          <Chip label={`+${plan.bonus_credits} Bonus`} size="small" sx={{ mt: 1, bgcolor: '#DCFCE7', color: '#059669', fontWeight: 'bold' }} />
                        )}
                      </Box>

                      {plan.description && (
                        <Typography variant="body2" color="#64748B" mb={3} sx={{ minHeight: '40px' }}>
                          {plan.description}
                        </Typography>
                      )}

                      <Box sx={{ mt: 'auto', mb: 3, bgcolor: '#F8FAFC', p: 2, borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="900" color="#1E293B">₹{plan.price_rupees}</Typography>
                        <Typography variant="caption" color="#94A3B8" fontWeight="600">One-time payment</Typography>
                      </Box>

                      <Button 
                        fullWidth 
                        variant={plan.is_popular ? "contained" : "outlined"}
                        disabled={isProcessing}
                        onClick={() => handlePurchase(plan.id, plan.total_credits, plan.price_rupees)}
                        startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <AddShoppingCart />}
                        sx={{ 
                          py: 1.5, borderRadius: '50px', fontWeight: 'bold', fontSize: '1rem',
                          background: plan.is_popular ? primaryGradient : 'transparent',
                          color: plan.is_popular ? 'white' : '#1E293B',
                          borderColor: '#CBD5E1',
                          '&:hover': { background: plan.is_popular ? primaryGradient : '#F1F5F9' },
                          '&.Mui-disabled': { bgcolor: '#E2E8F0' }
                        }}
                      >
                        {isProcessing ? 'Processing...' : 'Purchase / खरीदें'}
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', borderRadius: 3, justifyContent: 'center' }}>
            No credit plans available at the moment.
          </Alert>
        )}

      </Container>
    </Box>
  );
}